import { createElement, useCallback, useEffect, useState } from 'react';

import { SuggestionsDropdown } from './SuggestionsDropdown';

import type { SuggestionsDropdownProps } from './SuggestionsDropdown';
import type { FunctionComponentElement, RefObject } from 'react';
/**
 *
 * @param inputRef - Ref to the input element where autocomplete is applied.
 * @param mirroredRef - Ref to the mirrored element - used for calculating dimensions and positioning.
 * @param suggestions - Array of strings representing the suggestions to display in the dropdown.
 * @param trigger - The string that triggers the autocomplete functionality.
 * @param highlightRegex - Regular expression used to highlight parts of the input.
 *
 * @returns FunctionComponentElement<SuggestionsDropdownProps> | null
 *
 * @description
 * This hook provides autocomplete and highlighting functionality for an input textarea field.
 * In order to apply this the textarea must have a mirrored element behind it.
 * The two are kept in sync with each other.
 */
export const useInputAutocomplete = ({
  inputRef,
  suggestions = [],
  trigger,
  mirroredRef,
  updateControlledValue,
  highlightRegex,
}: {
  highlightRegex: RegExp;
  inputRef: RefObject<HTMLTextAreaElement> | null;
  mirroredRef: RefObject<HTMLDivElement> | null;
  suggestions?: string[];
  trigger: string;
  updateControlledValue?: (value: string) => void;
}) => {
  const [dropdown, setDropdown] =
    useState<FunctionComponentElement<SuggestionsDropdownProps> | null>(null);
  const [highlightedSuggestion, setHighlightedSuggestion] = useState(-1);

  const findIndexOfCurrentWord = useCallback(() => {
    const currentValue = inputRef?.current?.value;
    const cursorPos = inputRef?.current?.selectionStart;

    if (typeof cursorPos !== 'number' || !currentValue) {
      return 0;
    }

    let startIndex = cursorPos - 1;
    while (startIndex >= 0 && !/\s/.test(currentValue[startIndex])) {
      startIndex--;
    }

    return startIndex;
  }, [inputRef]);

  const handleSuggestionClick = useCallback(
    (suggestion: string) => {
      const currentValue = inputRef?.current?.value;
      const startIndex = findIndexOfCurrentWord();
      const cursorPos = inputRef?.current?.selectionStart;

      if (!currentValue || typeof cursorPos !== 'number') {
        return;
      }

      const newValue =
        currentValue.substring(0, startIndex + 1) +
        suggestion +
        currentValue.substring(cursorPos);

      updateControlledValue?.(newValue);

      setTimeout(() => setDropdown(null), 200);

      inputRef?.current?.focus();
      inputRef.current.selectionStart = inputRef.current.selectionEnd =
        startIndex + 1 + suggestion.length;
    },
    [findIndexOfCurrentWord, inputRef, updateControlledValue],
  );

  const handleInputEvent = useCallback(() => {
    if (!inputRef?.current || !mirroredRef?.current) return;

    const currentValue = inputRef.current.value;
    const cursorPos = inputRef.current.selectionStart;
    const startIndex = findIndexOfCurrentWord();

    const currentWord = currentValue.substring(startIndex + 1, cursorPos);

    if (typeof currentWord === 'string' && currentWord.startsWith(trigger)) {
      const textBeforeCursor = currentValue.substring(0, cursorPos);
      const textAfterCursor = currentValue.substring(cursorPos);

      const pre = document.createTextNode(textBeforeCursor);
      const post = document.createTextNode(textAfterCursor);
      const caretEle = document.createElement('span');
      caretEle.innerHTML = '&nbsp;';

      mirroredRef.current.innerHTML = '';
      mirroredRef.current.append(pre, caretEle, post);

      const dropdownTop = `${caretEle.offsetTop + caretEle.offsetHeight}px`;
      const dropdownLeft = `${caretEle.offsetLeft}px`;

      const handleBlur = () => {
        setDropdown(null);
        if (!inputRef?.current) return;

        inputRef.current.selectionStart = inputRef.current.selectionEnd =
          startIndex + 1;
      };

      const suggestionsElement = createElement(SuggestionsDropdown, {
        suggestions,
        left: dropdownLeft,
        top: dropdownTop,
        handleSuggestionClick,
        handleBlur,
        highlightedSuggestion,
      });

      setTimeout(() => setDropdown(suggestionsElement), 200);
    } else {
      setTimeout(() => setDropdown(null), 200);
    }
  }, [
    findIndexOfCurrentWord,
    handleSuggestionClick,
    highlightedSuggestion,
    inputRef,
    mirroredRef,
    suggestions,
    trigger,
  ]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!['ArrowDown', 'ArrowUp', 'Enter', 'Escape'].includes(e.key)) {
        return;
      }

      if (dropdown === null) {
        return;
      }

      e.preventDefault();
      switch (e.key) {
        case 'Escape':
          setHighlightedSuggestion(-1);
          setTimeout(() => setDropdown(null), 200);
          break;
        default:
          break;
      }
    },
    [dropdown],
  );

  const handleScroll = useCallback(() => {
    if (!mirroredRef?.current || !inputRef?.current) return;
    mirroredRef.current.scrollTop = inputRef.current.scrollTop;
  }, [inputRef, mirroredRef]);

  useEffect(() => {
    if (inputRef?.current && mirroredRef?.current) {
      // copy styles to match the input text
      const textareaStyles = window.getComputedStyle(inputRef.current);
      [
        'border',
        'boxSizing',
        'fontFamily',
        'fontSize',
        'fontWeight',
        'letterSpacing',
        'lineHeight',
        'padding',
        'textDecoration',
        'textIndent',
        'textTransform',
        'whiteSpace',
        'wordSpacing',
        'wordWrap',
      ].forEach((property) => {
        // @ts-ignore
        mirroredRef.current.style[property] = textareaStyles[property];
      });

      // keep the mirrored element's width and height in sync with the input
      const ro = new ResizeObserver(() => {
        if (!mirroredRef?.current || !inputRef?.current) return;

        const parseValue = (v: string) =>
          v.endsWith('px') ? parseInt(v.slice(0, -2), 10) : 0;
        const borderWidth = parseValue(textareaStyles.borderWidth);

        mirroredRef.current.style.width = `${inputRef.current.clientWidth + 2 * borderWidth}px`;
        mirroredRef.current.style.height = `${inputRef.current.clientHeight + 2 * borderWidth}px`;
      });
      ro.observe(inputRef.current);

      // keep the mirrored element's scroll position in sync with the input
      inputRef.current.addEventListener('scroll', handleScroll);

      inputRef?.current?.addEventListener('keydown', handleKeyDown);
      inputRef?.current?.addEventListener('input', handleInputEvent);

      const inputRefCopy = inputRef.current;

      return () => {
        inputRefCopy.removeEventListener('keydown', handleKeyDown);
        inputRefCopy.removeEventListener('input', handleInputEvent);
        inputRefCopy.removeEventListener('scroll', handleScroll);
      };
    }
  }, [inputRef, mirroredRef, handleInputEvent, handleKeyDown, handleScroll]);

  // keep the mirrored element's text content in sync with the input value & highlight
  useEffect(() => {
    if (!mirroredRef?.current || !inputRef) return;

    mirroredRef.current.textContent = inputRef?.current?.value || null;

    mirroredRef.current.innerHTML =
      inputRef?.current?.value.replace(
        highlightRegex,
        '<mark class="autocomplete-highlight">$&</mark>',
      ) ||
      inputRef?.current?.value ||
      '';
  }, [inputRef, inputRef?.current?.value, mirroredRef, highlightRegex]);

  return dropdown;
};
